import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useLoginMutation } from '../../store/api/authApi';
import { loginSuccess, loginFailure, setLoading } from '../../store/slices/authSlice';

const { Title } = Typography;

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();

  const onFinish = async (values: { username: string; password: string }) => {
    setError(null);
    dispatch(setLoading(true));

    try {
      const response = await login(values).unwrap();
      dispatch(loginSuccess(response));
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err?.data?.error || err?.error || 'Ошибка входа в систему';
      setError(errorMessage);
      dispatch(loginFailure());
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <Title level={2} className="mb-2" style={{ color: 'var(--text)' }}>
            Логистическая система v2.0
          </Title>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Вход в систему управления логистикой
          </p>
        </div>

        <Card className="shadow-xl" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              className="mb-6 border border-red-200"
            />
          )}

          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            layout="vertical"
            autoComplete="off"
            size="large"
          >
            <Form.Item
              label={<span className="text-gray-300 font-medium">Логин или Email</span>}
              name="username"
              rules={[
                {
                  required: true,
                  message: 'Пожалуйста, введите логин или email!',
                },
              ]}
            >
              <Input
                prefix={<UserOutlined className="text-gray-400" />}
                placeholder="Введите логин или email"
                className="placeholder-gray-400"
                style={{ backgroundColor: 'var(--card)', color: 'var(--text)', borderColor: 'var(--card-border)' }}
              />
            </Form.Item>

            <Form.Item
              label={<span className="text-gray-300 font-medium">Пароль</span>}
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Пожалуйста, введите пароль!',
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-gray-400" />}
                placeholder="Пароль"
                className="placeholder-gray-400"
                style={{ backgroundColor: 'var(--card)', color: 'var(--text)', borderColor: 'var(--card-border)' }}
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                className="h-12 bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 rounded-lg font-medium text-base shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Войти в систему
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-400">
            © 2024 Логистическая система. Все права защищены.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 